<template>
  <div class="search-page">
    <!-- 顶部背景横幅 -->
    <div class="page-banner"></div>

    <!-- 主体内容区 -->
    <div class="page-content">
      <!-- 面包屑 -->
      <div class="breadcrumb">
        <span class="breadcrumb-icon">🏠</span>
        <span class="breadcrumb-text">搜索内容：{{ keyword }}</span>
      </div>

      <div class="content-wrapper">
        <!-- 左侧菜单 -->
        <div class="sidebar">
          <div class="sidebar-title">搜索结果</div>
        </div>

        <!-- 右侧内容 -->
        <div class="main-content">
          <!-- 详情视图 -->
          <Detail
            v-if="showDetail"
            :title="detailData.title"
            :content="detailData.content"
            :author="detailData.author"
            :create_time="detailData.create_time"
            :showCloseBtn="true"
            @close="showDetail = false"
          />

          <!-- 列表视图 -->
          <div v-else class="list-container">
            <div v-if="loading" class="loading-tip">加载中...</div>
            <div v-else-if="listData.length === 0" class="empty-tip">暂无搜索结果</div>
            <div
              v-for="(item, index) in listData"
              :key="item.id"
              class="list-item"
              @click="handleItemClick(item)"
            >
              <div class="item-content">
                <h3
                  class="item-title"
                  :class="{ 'border-blue': index % 2 === 0, 'border-yellow': index % 2 !== 0 }"
                >
                  {{ item.title }}
                </h3>
                <p class="item-desc" v-html="item.content"></p>
                <div class="item-date">
                  <i class="icon-clock"></i>
                  <span>{{ item.create_time }}</span>
                </div>
              </div>
            </div>

            <!-- 分页 -->
            <div v-if="total > pagesize" class="pagination">
              <el-pagination
                background
                layout="prev, pager, next"
                :total="total"
                :page-size="pagesize"
                :current-page="page"
                @current-change="handlePageChange"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, computed } from 'vue'
import { useStore } from 'vuex'
import sys from '@/api/index'
import Detail from './detail.vue'

const store = useStore()

const keyword = computed(() => store.state.keyword)
const listData = ref([])
const loading = ref(false)
const showDetail = ref(false)
const detailData = ref({ title: '', content: '', author: '', create_time: '' })
const page = ref(1)
const pagesize = ref(10)
const total = ref(0)

const fetchList = async () => {
  if (!keyword.value) return
  loading.value = true
  try {
    const res = await sys.searchArticle({
      keyword: keyword.value,
      page: page.value,
      pagesize: pagesize.value
    })
    if (res && res.code === 2000 && res.data) {
      listData.value = res.data.data || []
      total.value = res.data.total || 0
    } else {
      listData.value = []
      total.value = 0
    }
  } catch (e) {
    console.error('搜索失败:', e)
    listData.value = []
  } finally {
    loading.value = false
  }
}

const handleItemClick = (item) => {
  detailData.value = {
    title: item.title || '',
    content: item.content || '',
    author: item.author || '',
    create_time: item.create_time || ''
  }
  showDetail.value = true
}

const handlePageChange = (val) => {
  page.value = val
  showDetail.value = false
  fetchList()
}

watch(keyword, () => {
  page.value = 1
  showDetail.value = false
  fetchList()
})

onMounted(() => {
  fetchList()
})
</script>

<style scoped>
.search-page {
  width: 100%;
  min-height: 100vh;
}

.page-banner {
  width: 100%;
  height: 210px;
  background: url('~@/assets/images/innerbanner.jpg') center center / cover no-repeat;
}

.page-content {
  max-width: 1400px;
  margin: -83px auto 0;
  padding: 0px 20px 60px;
  position: relative;
}

.breadcrumb {
  height: 80px;
  display: flex;
  align-items: center;
  font-size: 14px;
  background-color: #eaf7fa;
  padding: 10px 20px;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
}

.breadcrumb-icon {
  margin-right: 8px;
  font-size: 16px;
  color: #4facfe;
}

.breadcrumb-text {
  color: #666;
  font-size: 1rem;
}

.content-wrapper {
  display: flex;
}

.sidebar {
  width: 240px;
  flex-shrink: 0;
  background-color: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  height: fit-content;
}

.sidebar-title {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  color: #fff;
  font-size: 18px;
  font-weight: 500;
  padding: 20px 24px;
  text-align: left;
  border-radius: 8px 8px 0 0;
}

.main-content {
  flex: 1;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.list-container {
  padding: 20px;
}

.list-item {
  padding: 30px 40px;
  border-bottom: 1px solid #e8e8e8;
  transition: all 0.3s ease;
  cursor: pointer;
}

.list-item:hover {
  transform: translateX(8px);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.item-content {
  display: flex;
  flex-direction: column;
}

.item-title {
  font-size: 16px;
  font-weight: 500;
  color: #333;
  margin: 0 0 15px 0;
  padding-left: 12px;
  border-left: 4px solid transparent;
  line-height: 1.5;
  transition: all 0.3s ease;
}

.item-title:hover {
  color: #1890ff;
  padding-left: 16px;
}

.item-title.border-blue {
  border-left-color: #1890ff;
}

.item-title.border-yellow {
  border-left-color: #faad14;
}

.item-desc {
  font-size: 14px;
  color: #666;
  line-height: 1.8;
  margin: 0 0 15px 0;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.item-date {
  display: flex;
  align-items: center;
  font-size: 13px;
  color: #999;
}

.icon-clock {
  width: 14px;
  height: 14px;
  margin-right: 6px;
  background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="%23999" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>') no-repeat center;
  background-size: contain;
  flex-shrink: 0;
}

.loading-tip,
.empty-tip {
  text-align: center;
  padding: 60px 0;
  color: #999;
  font-size: 15px;
}

.pagination {
  display: flex;
  justify-content: center;
  padding: 30px 0 10px;
}

@media (max-width: 768px) {
  .content-wrapper {
    flex-direction: column;
  }

  .sidebar {
    width: 100%;
  }

  .list-item {
    padding: 20px;
  }

  .page-banner {
    height: 120px;
  }
}
</style>